import { Button, Combobox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Text } from "@sentricmusic/ui";
import { useCallback, useEffect } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import countries from "@/data/countries";

import type { VenueSearchResult } from "../types/gigs.types";
import type { GigClaimFormValues } from "../utils/gigsClaimSchema";

import useVenueSearch from "../hooks/useVenueSearch";

type VenueSectionProps = {
  adminRights: boolean;
  showManual: boolean;
  setShowManual: (show: boolean) => void;
};

function VenueSection({ adminRights, showManual, setShowManual }: VenueSectionProps) {
  const { t } = useTranslation(["gigs", "countries"]);
  const formContext = useFormContext<GigClaimFormValues>();
  const { venueOptions, setVenueOptions, venueLoading, selectedCountryRef, handleVenueSearch } = useVenueSearch();
  const country = formContext.watch("country");
  const venueName = formContext.watch("venueName");
  const { errors } = useFormState(formContext);

  const str = (s: string | null): string => s ?? "";

  // clear venue ui state when country changes
  const clearVenueOptions = useCallback(() => {
    setVenueOptions([]);
  }, [setVenueOptions]);

  useEffect(() => {
    clearVenueOptions();
  }, [country, clearVenueOptions]);

  useEffect(() => {
    selectedCountryRef.current = country ?? "";
  }, [country, selectedCountryRef]);

  // open add venue button if errror surfaces, for UX
  useEffect(() => {
    if ((errors.venueName || errors.address || errors.city || errors.postcode) && !showManual) {
      setShowManual(true);
    }
  }, [errors.venueName, errors.address, errors.city, errors.postcode, showManual, setShowManual]);

  return (
    <>
      {adminRights && (
        <div className="flex flex-col gap-2">
          <Text as="subtitle" className="">{t("gigs:select-country-placeholder", "Countries")}</Text>
          <FormField
            control={formContext.control}
            name="country"
            render={({ field }) => (
              <FormItem id="btnGigsClaimVenue">
                <FormControl>
                  <div id="btnGigsClaimCountry">
                    <Combobox
                      value={field.value ?? ""}
                      onValueChange={(selected) => {
                        selectedCountryRef.current = selected;
                        field.onChange(selected);
                      }}
                      data={countries.map(option => ({ label: t(`countries:${option.name}`), value: option.value }))}
                      searchByLabel
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>

            )}
          />
        </div>
      )}
      {(country != null) && (
        <div>
          <Text as="subtitle">{t("gigs:venue-details-header", "Venue details")}</Text>
          <div className="flex flex-col gap-2">

            <FormField
              control={formContext.control}
              name="venueName"
              render={() => (
                <FormItem>
                  <FormLabel required>{str(t("gigs:search-venue-placeholder", "Search for venue"))}</FormLabel>
                  <FormControl>
                    <Combobox
                      key={country}
                      smallFont
                      data={venueOptions}
                      // fire and forget on venue search
                      // eslint-disable-next-line ts/no-misused-promises
                      onSearchChange={handleVenueSearch}
                      isLoading={venueLoading}
                      onValueChange={(selected) => {
                        const details = JSON.parse(selected) as VenueSearchResult;
                        setShowManual(true);
                        formContext.setValue("venueId", details.id);
                        formContext.setValue("venuePremisesCategoryId", details.premisesCategoryId);
                        formContext.setValue("venueName", details.venueNameNew);
                        formContext.setValue("city", details.venueCity);
                        formContext.setValue("postcode", details.fullPostCode);
                        formContext.setValue("address", details.addressLine1);
                        formContext.setValue("venueIsApproved", details.isApproved);
                        formContext.setValue("prsId", details.prsVenueID);
                        formContext.setValue("venuePremisesTypeId", details.premisesTypeId);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!showManual && (
            // Show manual venue entry button
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowManual(true)}
                  variant="outline"
                  label={str(t("gigs:manually-add-venue", "Manually add venue address"))}
                />
              </div>
            )}
            {(showManual || (venueName != null && venueName !== "")) && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={formContext.control}
                  name="venueName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>
                        {str(t("gigs:venue-name-placeholder", "Venue name"))}
                      </FormLabel>
                      <FormControl>
                        <FormInput placeholder={str(t("gigs:venue-name-placeholder", "Venue name"))} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formContext.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{str(t("gigs:venue-address-placeholder", "Address"))}</FormLabel>
                      <FormControl>
                        <FormInput placeholder={str(t("gigs:venue-address-placeholder", "Address"))} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formContext.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{str(t("gigs:venue-city-placeholder", "City"))}</FormLabel>
                      <FormControl>
                        <FormInput placeholder={str(t("gigs:venue-city-placeholder", "City"))} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formContext.control}
                  name="postcode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{str(t("gigs:venue-postcode-placeholder", "Postcode"))}</FormLabel>
                      <FormControl>
                        <FormInput placeholder={str(t("gigs:venue-postcode-placeholder", "Postcode"))} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default VenueSection;
